import { useState } from "react";

import { ZodProvider } from "@autoform/zod";
import { ArrowDownToLine, Loader2 } from "lucide-react";

import { LoadingButton } from "@/components/common/loading-button";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { processAndUploadImage } from "@/lib/imgUtils";
import { useArtistCreateForm } from "@/pages/artists/create/hooks/use-artist-create-form";
import { useArtistCreateFormStore } from "@/pages/artists/create/hooks/use-artist-create-form";
import { mediaArtistSchema } from "@/pages/artists/schemas/artist.create.form";

const schemaProvider = new ZodProvider(mediaArtistSchema);

export const ArtistMediaStep = () => {
  const {
    id,
    spotifyEnrichmentStep,
    mediaStep,
    updateMedia,
    onMediaStepSubmit,
    loading,
  } = useArtistCreateForm();
  const [processingIndex, setProcessingIndex] = useState<number | null>(null);

  let spotifyPhotos: (string | undefined | null)[] = [];
  if (spotifyEnrichmentStep) {
    spotifyPhotos = [spotifyEnrichmentStep.imageUrl];
    spotifyPhotos.push(...(spotifyEnrichmentStep.galleryImagesUrl ?? []));
  }

  const handleSendToMedia = async (url: string, index: number) => {
    if (!id) return;
    setProcessingIndex(index);
    try {
      const storagePath = `Artists/${id}/${crypto.randomUUID()}.webp`;
      const {
        url: cdnUrl,
        blurhash,
        metadata,
      } = await processAndUploadImage(url, storagePath);
      const currentMedia = mediaStep?.media ?? { photos: [], teasers: [] };
      updateMedia({
        ...mediaStep!,
        media: {
          ...currentMedia,
          photos: [...currentMedia.photos, { url: cdnUrl, blurhash, metadata }],
        },
      });
    } catch (error) {
      console.error("Failed to process image", error);
    } finally {
      setProcessingIndex(null);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="text-md font-medium">Spotify Photos</div>
      <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-1.5 items-start">
        {spotifyPhotos &&
          spotifyPhotos.map(
            (url, index) =>
              url && (
                <div
                  key={index}
                  className="relative group overflow-hidden rounded cursor-pointer"
                  onClick={() =>
                    processingIndex === null && handleSendToMedia(url, index)
                  }
                >
                  <img
                    src={url}
                    alt={`Spotify Photo ${index + 1}`}
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {processingIndex === index ? (
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    ) : (
                      <ArrowDownToLine className="h-6 w-6 text-white" />
                    )}
                  </div>
                </div>
              ),
          )}
      </div>
      <AutoForm
        schema={schemaProvider}
        values={{ ...mediaStep, id }}
        onSubmit={(data, _) => {
          onMediaStepSubmit(data);
        }}
        {...injectCustomAutoFormComponents()}
      >
        <LoadingButton isLoading={loading.media === "loading"}>
          Create Artist
        </LoadingButton>
      </AutoForm>
    </div>
  );
};
